import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { PermissionContext } from "../../contexts/PermissionContext";
import { AuthContext } from "../../contexts/AuthContext";
import Notification from "../../components/UI/Notification";
import Modal from "../../components/UI/Modal";
import DeleteButton from "../../components/UI/AlertDialog";
import { getRoleBasedRoute } from "../../utils/routeUtils";

const ManagePermissions = () => {
  const { token, roles } = useContext(AuthContext); // For authentication token and user roles
  const { permissions, loadPermissions, addPermission, editPermission, removePermission } =
    useContext(PermissionContext); // PermissionContext methods
  const [newPermission, setNewPermission] = useState("");
  const [editPermissionData, setEditPermissionData] = useState(null);
  const [message, setMessage] = useState("");
  const [notification, setNotification] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Helper function to generate role-based routes
  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  // Load permissions on component mount
  useEffect(() => {
    loadPermissions(token);
  }, [token, loadPermissions]);

  // Handle new permission creation
  const handleCreatePermission = async () => {
    setMessage("");
    if (!newPermission.trim()) {
      // setNotification("Permission name cannot be empty.");
      setNotification({
        message: "Permission name cannot be empty.",
        type: "error",
      });
      return;
    }
    try {
      await addPermission(token, { name: newPermission });
      // setMessage("Permission created successfully.");
      // setNotification("Permission created successfully.");
      setNotification({
        message: "Permission created successfully.",
        type: "success",
      });
      setNewPermission("");
    } catch (err) {
      // setMessage(err.message || "Failed to create permission.");
      // setNotification(err.message || "Failed to create permission.");
      setNotification({
        message: `${err.message || "Failed to create permission."}`,
        type: "error",
      });
    }
  };

  // Open modal for updating permission
  const handleOpenModal = (permission) => {
    setEditPermissionData(permission);
    setIsModalOpen(true);
  };

  // Update permission
  const handleUpdatePermission = async () => {
    if (!editPermissionData || !editPermissionData.name.trim()) {
      // setNotification("Permission name cannot be empty.");
      setNotification({
        message: "Permission name cannot be empty.",
        type: "error",
      });
      return;
    }
    try {
      await editPermission(token, editPermissionData.id, {
        name: editPermissionData.name,
      });
      // setNotification("Permission updated successfully.");
      setNotification({
        message: "Permission updated successfully.",
        type: "success",
      });
      setIsModalOpen(false); // Close modal after update
    } catch (err) {
      // setNotification(err.message || "Failed to update permission.");
      setNotification({
        message: `${err.message || "Failed to update permission."}`,
        type: "error",
      });
    }
  };

  const handlePermissionDelete = async (id) => {
    try {
      await removePermission(token, id);
      // setNotification("Permission deleted successfully");
      setNotification({
        message: "Permission deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete permission");
      setNotification({
        message: "Failed to delete permission",
        type: "error",
      });
    }
  };

  return (
    <div className="p-6 flex flex-col items-center">
      {notification && (
          <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}
      <div className="w-full max-w-2xl bg-white dark:bg-gray-900 p-6 rounded shadow">
        
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white md:my-4">Manage Permissions</h2>
          <Link
            to={getRoleBasedRouteLocal('/admin/assign-permissions')}
            className="text-sm text-blue-500 hover:underline underline-offset-2 sm:mb-0 mb-2 text-center"
          >
            Assign Permissions
          </Link>
        </div>

        {message && (
          <p
            className={`text-sm font-medium mt-2 mb-4 ${
              message.includes("successfully") ? "text-green-600" : "text-red-600"
            }`}
          >
            {message}
          </p>
        )}

        <div className="flex flex-col gap-y-2 sm:flex-row items-stretch sm:items-center mb-6">
          <input
            type="text"
            placeholder="New Permission Name"
            value={newPermission}
            onChange={(e) => setNewPermission(e.target.value)}
            className="w-full px-4 py-2 dark:bg-black dark:text-white dark:border-gray-700 border border-gray-300 rounded sm:rounded-l-lg sm:rounded-r-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleCreatePermission}
            className="md:w-56 w-full px-4 py-2 bg-blue-600 text-white rounded sm:rounded-r-lg sm:rounded-l-none hover:bg-blue-700 transition duration-200"
          >
            Add Permission
          </button>
        </div>


        <h3 className="text-lg font-semibold text-gray-700 mb-4 dark:text-white">All Permissions</h3>

        {permissions.length === 0 ? (
          <p className="text-gray-500">No permissions available.</p>
        ) : (
          <ul className="space-y-2">
            {permissions.map((permission) => (
              <>
                <li key={permission.id} className="flex dark:text-white justify-between items-center text-gray-800">
                  {permission.name}
                  <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenModal(permission)}
                    className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4">
                    Edit
                  </button>
                  <DeleteButton style={`text-red-600 hover:text-red-800 hover:underline hover:underline-offset-4`} onDelete={() => handlePermissionDelete(permission.id)} />
                </div>
                </li>

              </>
            ))}
          </ul>

        )}

        {/* Update Permission Modal */}
        <Modal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          title="Update Permission"
        >
          <label>
          Permission name <span className='text-red-500'>*</span>
          </label>
          <input
            type="text"
            value={editPermissionData?.name || ""}
            onChange={(e) =>
              setEditPermissionData({ ...editPermissionData, name: e.target.value })
            }
            className="w-full px-3 py-2 dark:bg-black dark:text-white dark:border-gray-700 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex justify-between mt-4 space-x-2">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 dark:bg-gray-800 dark:text-white bg-gray-400 text-white rounded hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdatePermission}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Update Permission
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ManagePermissions;
